import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { FloatDatePicker } from 'ui-components/components/float-date-picker';
import { NumberInput } from 'ui-components/components/number-input';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';

import { BulkActions } from '../../../../enums/bulk-actions';
import { DateService } from '../../../../services/date-service';
import { messages } from '../messages';
import { DEFAULT_DAYS_TO_ADD, MIN_DAYS_TO_ADD, SET_SHIP_BY_DATE, SET_SHIP_BY_DAYS } from './constants';
import style from './ship-by-date.module.css';
import { convertDateToMomentDateObjAtMidnightInSellerTimeZone } from '../../../../services/datetime-service';
import Global from '../../../../store/global-selectors';

const ShipByDateComponent = ({ onChange, model }) => {
  const userDateFormat = useSelector(Global.selectUserUserDateFormat);
  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);
  const todaysDate = convertDateToMomentDateObjAtMidnightInSellerTimeZone(moment(), sellerTimeZone).toISOString();
  const shipByDate = model?.shipByDate;
  const daysToAdd = model?.daysToAdd;
  const dateInputMethod = model?.dateInputMethod || SET_SHIP_BY_DATE;

  const handleInputDate = (value) => {
    onChange({
      ...model,
      shipByDate: value,
      daysToAdd: null,
    });
  };

  const handleInputDays = (value) => {
    onChange({
      ...model,
      shipByDate: null,
      daysToAdd: value,
    });
  };

  const handleOnCheckedDate = () => {
    onChange({
      ...model,
      dateInputMethod: SET_SHIP_BY_DATE,
      shipByDate: todaysDate,
      daysToAdd: null,
    });
  };

  const handleOnCheckedDays = () => {
    onChange({
      ...model,
      daysToAdd: DEFAULT_DAYS_TO_ADD,
      dateInputMethod: SET_SHIP_BY_DAYS,
    });
  };

  const dateInput = () => {
    return (
      <FloatDatePicker
        additionalDateFormats={DateService.getAdditionalDateFormats()}
        allowPastDates
        className={style['set-ship-by-date']}
        dateFormat={userDateFormat}
        label={userDateFormat}
        onChange={handleInputDate}
        sellerTimeZone={sellerTimeZone}
        value={shipByDate || ''}
      />
    );
  };

  const daysInput = () => {
    return (
      <div className={style['set-ship-by-days']}>
        <NumberInput
          className={style['days-input']}
          min={MIN_DAYS_TO_ADD}
          onArrowButtonPress={handleInputDays}
          onChange={handleInputDays}
          value={daysToAdd || MIN_DAYS_TO_ADD}
        />
        <div className={style['days-input-label']}>
          <FormattedMessage {...messages.shipByDateDaysInputLabel} />
        </div>
      </div>
    );
  };

  return (
    <div>
      <h4 className={style['ship-by-date-label']}>
        <FormattedMessage {...messages.shipByDateLabel} />
      </h4>
      <RadioCheckListItem isChecked={dateInputMethod === SET_SHIP_BY_DATE} onChecked={handleOnCheckedDate}>
        <FormattedMessage {...messages.shipByDateExactDateRadioLabel} />
      </RadioCheckListItem>
      {dateInputMethod === SET_SHIP_BY_DATE && dateInput()}
      <RadioCheckListItem isChecked={dateInputMethod === SET_SHIP_BY_DAYS} onChecked={handleOnCheckedDays}>
        <FormattedMessage {...messages.shipByDateDaysAfterOrderDateRadioLabel} />
      </RadioCheckListItem>
      {dateInputMethod === SET_SHIP_BY_DAYS && daysInput()}
    </div>
  );
};

ShipByDateComponent.propTypes = {
  onChange: PropTypes.func.isRequired,
  model: PropTypes.object.isRequired,
};

export const ShipByDate = {
  id: BulkActions.shipByDate.name,

  render: (onChange, model) => {
    return <ShipByDateComponent model={model} onChange={onChange} />;
  },

  getErrorsFromModel: (model) => {
    const { shipByDate, daysToAdd, dateInputMethod } = model;
    if (dateInputMethod === SET_SHIP_BY_DATE && !shipByDate) {
      return [
        {
          errorType: 'bulk:needs-ship-by-date',
          defaultMessage: messages.shipByDateRequired,
        },
      ];
    }
    if (dateInputMethod === SET_SHIP_BY_DAYS && !daysToAdd) {
      return [
        {
          errorType: 'bulk:needs-ship-by-date',
          defaultMessage: messages.shipByDaysRequired,
        },
      ];
    }
    if (!dateInputMethod && !shipByDate) {
      return [
        {
          errorType: 'bulk:needs-ship-by-date',
          defaultMessage: messages.shipByDateRequired,
        },
      ];
    }
  },
};
